/**
 * FS-11-03 매칭·팀 편성 — 편성 초안(순수 함수 · 화면·시험 공용).
 *  - 슬롯 = 팀(협동 · 크기 2~4) 또는 쌍(밀어내기 · 크기 2 고정)
 *  - 편성할 수 있는 학생 = 연결 정상 + 기계 있음(끊김·미접속·분리 학생은 자동으로 미배정 — 서버에 보내지 않는다)
 *  - 무작위 = 섞어서 슬롯에 돌려 담기(팀 크기 차이 ≤1 · 1명 팀이 생기지 않게) · 초기화 = 모두 미배정
 * 점수·순위로 나누지 않는다(DR-01) — 순서는 이름순 또는 무작위뿐.
 */
import type { ConsoleStudent, TeamKind, TeamSize, TeamDraft, TeamPayloadResult } from "../types";

export const TEAM_SIZES: readonly TeamSize[] = [2, 3, 4];

export const canJoin = (s: ConsoleStudent) => s.connection === "online" && s.machine !== null;

const byName = (a: ConsoleStudent, b: ConsoleStudent) =>
  a.nickname.localeCompare(b.nickname, "ko") || a.pid.localeCompare(b.pid);

/** 슬롯 수 — 편성 가능한 학생을 다 담을 만큼(최소 1) · 쌍은 남는 1명을 대기열로 */
export function slotCount(n: number, kind: TeamKind, size: TeamSize): number {
  if (kind === "pairs") return Math.max(1, Math.floor(n / 2));
  return Math.max(1, Math.ceil(n / size));
}

/** 서버가 지금 쓰는 편성(협동 activity.team)으로 시작 — 없으면 빈 슬롯 */
export function initialDraft(students: readonly ConsoleStudent[], kind: TeamKind): TeamDraft {
  const ok = students.filter(canJoin).sort(byName);
  if (kind === "pairs") return { kind, size: 2, slots: empty(slotCount(ok.length, kind, 2)) };
  const groups = new Map<number, string[]>();
  for (const s of ok)
    if (s.activity?.kind === "team") {
      const g = groups.get(s.activity.team) ?? [];
      g.push(s.pid);
      groups.set(s.activity.team, g);
    }
  const found = [...groups.entries()].sort((a, b) => a[0] - b[0]).map(([, g]) => g);
  const biggest = Math.max(0, ...found.map((g) => g.length));
  const size = (biggest >= 2 && biggest <= 4 ? biggest : 3) as TeamSize;
  // 서버 편성이 있으면 그 팀 수 그대로(와이어: 팀 4개 + 미배정) · 없으면 학생 수에 맞춘 빈 슬롯
  const n = found.length || slotCount(ok.length, kind, size);
  const slots = empty(n);
  found.forEach((g, i) => (slots[i] = g.slice(0, size)));
  return { kind, size, slots };
}

const empty = (n: number): string[][] => Array.from({ length: n }, () => []);

/** 연결이 끊기거나 분리된 학생 · 명단에서 빠진 학생은 슬롯에서 뺀다(미배정으로) */
export function prune(d: TeamDraft, students: readonly ConsoleStudent[]): TeamDraft {
  const ok = new Set(students.filter(canJoin).map((s) => s.pid));
  let changed = false;
  const slots = d.slots.map((g) => {
    const kept = g.filter((p) => ok.has(p));
    if (kept.length !== g.length) changed = true;
    return kept;
  });
  return changed ? { ...d, slots } : d;
}

/** 팀 크기 바꾸기 — 넘치는 학생·없어진 슬롯의 학생은 미배정으로 */
export function resize(d: TeamDraft, size: TeamSize, eligible: number): TeamDraft {
  if (d.kind === "pairs" || size === d.size) return d;
  const n = slotCount(eligible, d.kind, size);
  const slots = empty(n).map((_, i) => (d.slots[i] ?? []).slice(0, size));
  return { ...d, size, slots };
}

/** pid 를 슬롯 to 로(null = 미배정). 자리가 없으면 그대로 */
export function move(d: TeamDraft, pid: string, to: number | null): TeamDraft {
  if (to !== null && (to < 0 || to >= d.slots.length)) return d;
  const from = d.slots.findIndex((g) => g.includes(pid));
  if (from === to) return d;
  if (to !== null && d.slots[to]!.length >= d.size) return d;
  const slots = d.slots.map((g) => g.filter((p) => p !== pid));
  if (to !== null) slots[to] = [...slots[to]!, pid];
  return { ...d, slots };
}

/** 초기화 — 모두 미배정(슬롯 수는 지금 편성할 수 있는 학생 수에 맞춘다) */
export const clear = (d: TeamDraft, eligible: number): TeamDraft => ({
  ...d,
  slots: empty(slotCount(eligible, d.kind, d.size)),
});

/** 무작위 편성 — 편성 가능한 학생을 섞어 슬롯에 돌려 담는다(크기 차이 ≤1) */
export function randomize(
  d: TeamDraft,
  students: readonly ConsoleStudent[],
  rnd: () => number = Math.random,
): TeamDraft {
  const pids = students.filter(canJoin).map((s) => s.pid);
  for (let i = pids.length - 1; i > 0; i--) {
    const j = Math.floor(rnd() * (i + 1));
    [pids[i], pids[j]] = [pids[j]!, pids[i]!];
  }
  const n = slotCount(pids.length, d.kind, d.size);
  const slots = empty(n);
  const cap = d.kind === "pairs" ? n * 2 : pids.length;
  pids.slice(0, cap).forEach((p, i) => slots[i % n]!.push(p));
  return { ...d, slots };
}

/** 슬롯에 없는 학생(이름순) — 끊긴·분리 학생 포함 */
export function unassigned(d: TeamDraft, students: readonly ConsoleStudent[]): ConsoleStudent[] {
  const inSlot = new Set(d.slots.flat());
  return students.filter((s) => !inSlot.has(s.pid)).sort(byName);
}

/** 적용 본문 — 빈 슬롯은 빼고, 1명(쌍은 2명이 아닌) 슬롯이 있으면 막는다 */
export function payload(d: TeamDraft): TeamPayloadResult {
  const used = d.slots.filter((g) => g.length > 0);
  if (!used.length) return { ok: false, reason: "none" };
  if (d.kind === "pairs") {
    if (used.some((g) => g.length !== 2)) return { ok: false, reason: "small" };
    return { ok: true, body: { kind: "pairs", pairs: used.map((g) => [g[0]!, g[1]!]) } };
  }
  if (used.some((g) => g.length < 2 || g.length > 4)) return { ok: false, reason: "small" };
  return { ok: true, body: { kind: "teams", teams: used.map((g) => [...g]) } };
}

/** 같은 편성인가(적용됨 표시) */
export const sameDraft = (a: TeamDraft, b: TeamDraft) =>
  JSON.stringify(a.slots.filter((g) => g.length)) ===
  JSON.stringify(b.slots.filter((g) => g.length));

/** 협동 물체(팀 크기 → 무게 · 룸 모드 carry-together 프리셋과 같은 규칙) */
export const cargoOf = (n: number) => (n <= 2 ? "box" : n === 3 ? "log" : "big-box");
